// src/lib/richtext/tiptapConverters.js
import { generateHTML } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import { Highlight } from '@tiptap/extension-highlight'

// TipTap extensions for HTML generation
const extensions = [
  StarterKit.configure({
    bulletList: { keepMarks: true },
    orderedList: { keepMarks: true },
    heading: { levels: [1, 2, 3] }
  }),
  Link.configure({ 
    openOnClick: true, 
    autolink: true, 
    linkOnPaste: true 
  }),
  Underline,
  TextStyle,
  Color,
  Highlight
]

/**
 * Convert TipTap JSON document to HTML
 * @param {Object} docJson - TipTap document JSON
 * @returns {string} HTML string
 */
export function toHtmlFromJson(docJson) {
  try {
    return generateHTML(docJson || { type: 'doc', content: [] }, extensions)
  } catch (error) {
    console.warn('Failed to convert TipTap JSON to HTML:', error)
    return ''
  }
}

/**
 * Convert HTML to plain text
 * @param {string} html - HTML string
 * @returns {string} Plain text
 */
export function toPlainTextFromHtml(html) {
  try {
    const div = typeof document !== 'undefined' ? document.createElement('div') : null
    if (!div) return ''
    div.innerHTML = html || ''
    return (div.textContent || '').trim()
  } catch (error) {
    console.warn('Failed to convert HTML to plain text:', error)
    return ''
  }
}

/**
 * Convert legacy Draft.js content to TipTap format
 * @param {Object} draftContent - Draft.js content object
 * @returns {Object} TipTap document JSON
 */
export function convertDraftToTipTap(draftContent) {
  try {
    // If it's already TipTap format, return as-is
    if (draftContent && draftContent.type === 'doc') {
      return draftContent
    }
    
    // For now, create a simple paragraph with the text content
    // This is a basic conversion - in production you might want more sophisticated conversion
    const text = draftContent?.text || draftContent?.content_plain || ''
    
    return {
      type: 'doc',
      content: text ? [
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: text
            }
          ]
        }
      ] : []
    }
  } catch (error) {
    console.warn('Failed to convert Draft.js to TipTap:', error)
    return {
      type: 'doc',
      content: []
    }
  }
}

/**
 * Ensure content is in TipTap format
 * @param {Object} content - Content object with various possible formats
 * @returns {Object} Normalized content object
 */
export function ensureTipTapFormat(content) {
  
  // If content accidentally arrives as the literal string "[object Object]",
  // treat it as empty to avoid rendering garbage text in the editor.
  if (typeof content === 'string') {
    if (content.trim() === '[object Object]') {
      return {
        content_json: { type: 'doc', content: [] },
        content_html: '<p></p>',
        content_plain: ''
      }
    }
    // Plain string (not HTML markup) -> wrap in a paragraph
    const isLikelyHtml = /<\w+[^>]*>/.test(content)
    const result = isLikelyHtml
      ? { content_json: null, content_html: content, content_plain: toPlainTextFromHtml(content) }
      : {
          content_json: {
            type: 'doc',
            content: content ? [{ type: 'paragraph', content: [{ type: 'text', text: content }] }] : []
          },
          content_html: content ? `<p>${content}</p>` : '<p></p>',
          content_plain: content
        }
    return result;
  }

  if (!content) {
    return {
      content_json: { type: 'doc', content: [] },
      content_html: '<p></p>',
      content_plain: ''
    }
  }


  // If we already have TipTap JSON, use it
  if (content.content_json && content.content_json.type === 'doc') {
    const result = {
      content_json: content.content_json,
      content_html: content.content_html || toHtmlFromJson(content.content_json),
      content_plain: content.content_plain || toPlainTextFromHtml(content.content_html)
    };
    return result;
  }

  // If we have HTML, let TipTap parse it directly
  if (content.content_html || content.html) {
    const html = content.content_html || content.html
    const result = {
      content_json: null, // Let TipTap parse the HTML directly
      content_html: html,
      content_plain: content.content_plain || content.text || toPlainTextFromHtml(html)
    };
    return result;
  }

  // If we only have text, create a simple paragraph
  if (content.content_plain || content.text) {
    const text = content.content_plain || content.text
    return {
      content_json: {
        type: 'doc',
        content: text ? [
          {
            type: 'paragraph',
            content: [
              {
                type: 'text',
                text: text
              }
            ]
          }
        ] : []
      },
      content_html: text ? `<p>${text}</p>` : '<p></p>',
      content_plain: text || ''
    }
  }

  // Default empty content
  return {
    content_json: { type: 'doc', content: [] },
    content_html: '<p></p>',
    content_plain: ''
  }
}
